import { Box, Flex, Text } from "@chakra-ui/react";
import React from "react";
import { IoCheckmarkSharp } from "react-icons/io5";

import { WizardStep } from "../AiInterviewerWizardContext";

interface FlexIntakeVerticalStepperProps {
  currentStep: number;
  steps: WizardStep[];
  activeStepDescription?: string;
  title?: string;
  hideTitle?: boolean;
}

type StepState = "completed" | "active" | "upcoming";

/**
 * Vertical wizard stepper for the Flex Intake flow.
 *
 * Figma: Screen | Admin IX (Feb), node 20141-124892
 * - Sidebar: 180px fixed, 24px left padding, transparent bg
 * - Header: Inter SemiBold 11.67px/16px, #808B97, uppercase
 * - Circle: 24×24px, 1.5px stroke, border-radius full
 *   - Completed/Active: fill + stroke #60CCA5, white number/checkmark
 *   - Upcoming: no fill, stroke #D3D8DE, #B8BEC5 number
 * - Label: Inter 11.67px/16px, 8px gap from circle
 *   - Active: SemiBold #2DB282, description below (Regular #393F47)
 *   - Upcoming: Regular #B8BEC5
 * - Connectors: 2px wide vertical lines between circles
 */

const FlexIntakeVerticalStepper: React.FC<FlexIntakeVerticalStepperProps> = ({
  currentStep,
  steps,
  activeStepDescription,
  title,
  hideTitle,
}) => {
  const notificationsIndex = steps.findIndex(
    (s) => s.label === "Notifications"
  );
  const stepperSteps =
    notificationsIndex >= 0
      ? steps.filter((s) => s.label !== "Notifications")
      : steps;
  const stepperStep =
    notificationsIndex >= 0 && currentStep > notificationsIndex
      ? currentStep - 1
      : currentStep === notificationsIndex
      ? notificationsIndex - 1
      : currentStep;

  return (
    <Flex
      direction="column"
      w="180px"
      flexShrink={0}
      pl="24px"
      overflowY="auto"
    >
      {/* Header */}
      {!hideTitle && (
        <Text
          fontFamily="Inter, sans-serif"
          fontSize="11.67px"
          fontWeight={600}
          lineHeight="16px"
          color="#808B97"
          textTransform="uppercase"
          textAlign="left"
        >
          {title || "New AI Interviewer"}
        </Text>
      )}

      {/* Stepper */}
      <Flex direction="column" mt="8px">
        {stepperSteps.map((step, index) => {
          const isFirst = index === 0;
          const isLast = index === stepperSteps.length - 1;
          const state: StepState =
            index < stepperStep
              ? "completed"
              : index === stepperStep
              ? "active"
              : "upcoming";

          return (
            <Flex key={step.label} direction="row" align="stretch">
              {/* Circle + connector column */}
              <Flex direction="column" align="center" w="24px" flexShrink={0}>
                {/* Top connector line */}
                <Box
                  w="2px"
                  h="24px"
                  bg={isFirst ? "transparent" : "#D3D8DE"}
                  flexShrink={0}
                />
                {/* Circle node */}
                <StepCircle state={state} stepNumber={index + 1} />
                {/* Bottom connector line */}
                {!isLast && <Box w="2px" flex="1" minH="24px" bg="#D3D8DE" />}
              </Flex>

              {/* Label + description column */}
              <Flex
                direction="column"
                ml="8px"
                pt="28px"
                pb={isLast ? "0" : "8px"}
              >
                <Text
                  fontFamily="Inter, sans-serif"
                  fontSize="11.67px"
                  fontWeight={state === "upcoming" ? 400 : 600}
                  lineHeight="16px"
                  color={state === "upcoming" ? "#B8BEC5" : "#2DB282"}
                  whiteSpace="nowrap"
                >
                  {step.label}
                </Text>
                {state === "active" &&
                  (activeStepDescription || step.description) && (
                    <Text
                      fontFamily="Inter, sans-serif"
                      fontSize="11.67px"
                      fontWeight={400}
                      lineHeight="16px"
                      color="#393F47"
                      mt="8px"
                    >
                      {activeStepDescription || step.description}
                    </Text>
                  )}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
};

type StepCircleProps = {
  state: StepState;
  stepNumber: number;
};

const StepCircle: React.FC<StepCircleProps> = ({ state, stepNumber }) => {
  const isGreen = state === "completed" || state === "active";

  return (
    <Flex
      align="center"
      justify="center"
      borderRadius="full"
      w="24px"
      h="24px"
      flexShrink={0}
      bg={isGreen ? "#60CCA5" : "transparent"}
      border="1.5px solid"
      borderColor={isGreen ? "#60CCA5" : "#D3D8DE"}
    >
      {state === "completed" ? (
        <Box as={IoCheckmarkSharp} color="white" boxSize="14px" />
      ) : (
        <Text
          fontFamily="Inter, sans-serif"
          fontSize="9.72px"
          fontWeight={500}
          lineHeight="12px"
          color={isGreen ? "white" : "#B8BEC5"}
          textAlign="center"
        >
          {stepNumber}
        </Text>
      )}
    </Flex>
  );
};

export { FlexIntakeVerticalStepper };
